import { CashFlow, CashFlowType } from '@api/CashFlows/CashFlow';

// ============================================================================
// Los textos de la bitácora de CAJAS (M3 del `../PLAN-BITACORA-ACTIVITY-LOGS.md`).
//
// Vive aparte y es PURO por la misma razón que `CatalogActivity`: de estas frases
// depende que un descuadre se pueda reconstruir meses después, así que se prueban
// sin Firestore y se leen todas juntas — si una dice "$1500.5" y la de al lado
// "$1,500.50", el mismo corte se lee de dos maneras.
//
// ## La regla del dinero en la bitácora
// **Siempre dos decimales y separador de miles.** No es cosmético: el renglón de
// un ajuste de caja ES la evidencia, y `$1500.5` invita a leer mil quinientos con
// cinco. Los textos que ya existían usaban `$${amount}` a pelo (la interpolación
// de un número), que es de donde salía esa mezcla.
// ============================================================================

/** Dinero como se escribe en la bitácora: `$1,500.50`, siempre. */
export const cashText = (amount?: number): string =>
    `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`;

const TIPO_DE_MOVIMIENTO: Record<CashFlowType, string> = {
    [CashFlowType.Input]: 'un ingreso',
    [CashFlowType.Output]: 'un gasto',
    [CashFlowType.Shipping]: 'un gasto de envío'
};

/**
 * El renglón de un movimiento de caja BORRADO.
 *
 * ⚠️ Lleva el monto, el tipo y la descripción **dentro del texto** a propósito:
 * cuando alguien lo lea, el documento del movimiento ya no existe. Es el caso
 * contrario al de un cambio de precio —donde el objeto sigue ahí y basta decir
 * qué cambió—: aquí el renglón es lo ÚNICO que queda.
 */
export const describeDeletedCashFlow = (cashFlow: Pick<CashFlow, 'type' | 'amount' | 'description'>): string => {
    const tipo = TIPO_DE_MOVIMIENTO[cashFlow.type] || 'un movimiento';
    const descripcion = (cashFlow.description || '').trim();
    const detalle = descripcion ? ` ("${descripcion}")` : '';
    return `borró ${tipo} de ${cashText(cashFlow.amount)}${detalle} del corte`;
};

/**
 * El renglón que deja en la CAJA el purgado de un pedido.
 *
 * ⚠️ El purgado ya escribe su bitácora en el pedido… que se borra en el mismo
 * acto. Sin esto, la caja perdía un ingreso y no quedaba una sola línea que
 * dijera por qué — justo lo que alguien busca cuando el corte no cuadra.
 */
export const describePurgedOrderCashFlows = (amount: number): string =>
    `se quitó ${cashText(amount)} del corte al eliminar un pedido`;
